import { useCallback, useEffect, useState } from "react";
import { listManufacturingAuditLog } from "@/lib/manufacturingClient";
import type { AuditLogEntry, MfgAuditListFilters } from "@/lib/manufacturing/types";

/**
 * Loads the most recent audit_log entries for the workspace org. Backed by
 * the admin-gated `/api/mfg/audit` endpoint, so the hook will surface a 403
 * `error` for non-admin sessions instead of throwing.
 *
 * Pagination is left to a follow-up: today the panel just shows the latest
 * `limit` entries and a Refresh button. When/if we want infinite scroll we
 * extend this hook with `loadMore`.
 */
export function useManufacturingAuditLog(opts: {
  enabled: boolean;
  filters?: MfgAuditListFilters;
}) {
  const { enabled } = opts;
  const filters = opts.filters;
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [working, setWorking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setWorking(true);
    setError(null);
    const r = await listManufacturingAuditLog(filters ?? {});
    setWorking(false);
    if (!r.ok) {
      // 403 from a non-admin viewer is expected; show a friendly note.
      setError(r.status === 403 ? "Activity log is admin/owner only." : r.error);
      setEntries([]);
      setTotal(0);
      return;
    }
    setEntries(r.data.entries);
    setTotal(r.data.total);
  }, [enabled, filters]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setEntries([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    void (async () => {
      const r = await listManufacturingAuditLog(filters ?? {});
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.status === 403 ? "Activity log is admin/owner only." : r.error);
        setEntries([]);
        setTotal(0);
        return;
      }
      setEntries(r.data.entries);
      setTotal(r.data.total);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, filters]);

  return { entries, total, loading, working, error, refresh };
}
